<template>
    <div
        class="product-card-price"
        :class="{
            'product-card-price--openers-price': hasOpenersPrice,
            'product-card-price--discount-above-50': percentageDiscount >= 50,
            'product-card-price--discount-above-20': percentageDiscount >= 20,
            'product-card-price--special': hasSpecialPrice,
            'product-card-price--normal': !hasOpenersPrice && !hasSpecialPrice && !percentageDiscount,
            'product-card-price--xl': size === 'xl',
            'product-card-price--l': size === 'l',
        }"
    >
        <div class="product-card-price__prices">
            <!--            <b v-if="priceFrom" v-html="`${$t('productDetail.fromPrice', { price: $formatCurrency(priceFrom) })}`"></b>-->
            <b v-if="price" class="notranslate" v-html="$formatCurrency(price)"></b>
            <s
                v-if="priceBeforeDiscount && priceBeforeDiscount > price && !hasSpecialPrice"
                class="product-card-price__old notranslate"
                v-html="$formatCurrency(priceBeforeDiscount)"
            ></s>
        </div>
        <div
            v-if="hasSpecialPrice || hasOpenersPrice"
            class="product-card-price__special-box"
            :class="{
                'product-card-price__special-box--opened': specialInfoBoxOpened,
                'product-card-price__special-box--openers-price': hasOpenersPrice,
            }"
            @click.stop.prevent="specialBoxClicked"
        >
            <span v-if="hasOpenersPrice">Wow!</span>
            <span v-else-if="hasSuperPrice">{{ $t('productDetail.superPrice') }}</span>
            <template v-else-if="hasCompetitorSuperPrice">
                <div class="product-card-price__special-box__info-box">
                    {{ $t('productDetail.competitorPriceDescription') }}
                </div>
                <span>{{ $t('productDetail.competitorPrice') }}</span>
            </template>
            <div
                v-else-if="percentageDiscount"
                class="product-card-price__special-box__discount"
                :class="{
                    'product-card-price__special-box__discount--above-50': percentageDiscount >= 50,
                    'product-card-price__special-box__discount--above-20': percentageDiscount >= 20,
                }"
            >
                -{{ percentageDiscount }} %
            </div>
        </div>
        <div v-else-if="percentageDiscount" class="product-card-price__discount">-{{ percentageDiscount }} %</div>
    </div>
</template>

<script setup lang="ts">
const props = defineProps<{
    hasSuperPrice: boolean
    hasOpenersPrice: boolean
    hasCompetitorSuperPrice: boolean
    price: number
    priceBeforeDiscount: number | null
    addonSaleBehavior: boolean
    addonSalePrice: number | null
    priceFrom: number | null
    size?: string | null
}>()

const specialInfoBoxOpened = ref(false)
const specialBoxClicked = () => {
    specialInfoBoxOpened.value = !specialInfoBoxOpened.value
}

const hasSpecialPrice = computed(() => {
    return props.hasSuperPrice || props.hasCompetitorSuperPrice
})

const priceBeforeDiscount = computed(() => {
    if (props.addonSaleBehavior) {
        return props.priceBeforeDiscount ? Math.max(props.priceBeforeDiscount, props.price) : props.price
    }

    return Math.max(props.priceBeforeDiscount || 0, props.price)
})

// const priceFrom = computed(() => {
//     return props.addonSaleBehavior ? null : props.priceFrom
// })

const price = computed(() => {
    return props.addonSaleBehavior && props.addonSalePrice ? props.addonSalePrice : props.price
})

const percentageDiscount = computed(() => {
    if (priceBeforeDiscount.value === null) {
        return 0
    }

    return 100 - Math.ceil(Math.round((price.value / (priceBeforeDiscount.value / 100)) * 100) / 100)
})
</script>

<style scoped lang="scss">
@import '../../../node_modules/bootstrap/scss/functions';
@import '../../../node_modules/bootstrap/scss/variables';
@import '../../../node_modules/bootstrap/scss/mixins';

.product-card-price {
    display: inline-flex;
    background: $color-secondary-accent-green;
    border-radius: 10px;
    line-height: 30px;
    margin: 0 0 5px;
    font-size: 14px;

    @container (min-width: 180px) {
        font-size: 17px;
    }

    @container (min-width: 190px) {
        font-size: 20px;
    }

    @container (min-width: 280px) {
        font-size: 20px;
    }

    &__prices {
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 0 3px;
        border-radius: 8px 0 0 8px;
        border: 1px solid $color-tercial-light-green-3;
        background: $color-tercial-light-green-3; //$color-white;
        color: $color-primary-green;
        white-space: nowrap;

        @container (min-width: 180px) {
            padding: 0 5px;
            gap: 5px;
        }

        @container (min-width: 190px) {
            gap: 10px;
            padding: 0 5px;
        }

        @container (min-width: 280px) {
            gap: 10px;
            padding: 0 8px;
        }

        b {
            font-weight: 600;
        }
    }

    &__discount {
        align-self: center;
        padding: 0 4px;
        color: #000;
        font-weight: 500;
        font-size: 11px;
        white-space: nowrap;

        @container (min-width: 190px) {
            font-size: 15px;
            padding: 0 5px;
        }

        @container (min-width: 280px) {
            font-size: 15px;
            font-weight: 500;
            padding: 0 10px;
        }
    }

    &--discount-above-20 {
        background: $color-secondary-accent-green;
    }

    &--discount-above-50 {
        background: #000;

        .product-card-price__discount {
            color: $color-white;
        }
    }

    &--special {
        background: $color-primary-green;
        color: #fff;
        margin-bottom: 11px;

        .product-card-price__prices {
            border: 1px solid $color-primary-pink;
            background: $color-primary-pink;
            color: $color-primary-green;
        }
    }

    &--xl {
        font-size: 25px;
        line-height: 34px;
        margin: 0;

        .product-card-price {
            &__old {
                font-size: 14px;
            }

            &__discount {
                font-size: 13px;
            }

            &__prices {
                padding: 0 8px;
            }

            &__special-box {
                padding: 0 6px;
                font-size: 15px;
                line-height: 17px;
            }
        }
    }

    &--l {
        font-size: 20px;
        line-height: 34px;
        margin: 0;

        .product-card-price {
            &__old {
                font-size: 13px;
            }

            &__discount {
                font-size: 13px;
            }

            &__prices {
                padding: 0 8px;
            }

            &__special-box {
                padding: 0 6px;
                font-size: 15px;
                line-height: 17px;
            }
        }
    }

    &--normal {
        background: transparent;

        .product-card-price__prices {
            padding: 0;
            border: 1px solid transparent;
            color: $color-dark;
            background: transparent;
        }
    }

    &--openers-price {
        background: $color-cta;
        color: $color-white;
        margin-bottom: 11px;

        .product-card-price__prices {
            background: $color-primary-pink;
            color: $color-primary-green;
            border: 1px solid $color-primary-pink;
        }
    }

    &__old {
        font-size: 11px;
        color: $color-grey-500;

        @container (min-width: 190px) {
            font-size: 13px;
        }

        @container (min-width: 280px) {
            font-size: 14px;
        }
    }

    &__special-box {
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 0 6px 0 6px;
        font-size: 12px;
        background: $color-primary-green;
        color: $color-white;
        border-radius: 10px;
        line-height: 13px;
        position: relative;

        @container (min-width: 190px) {
            font-size: 14px;
            gap: 10px;
            text-transform: none;
            padding: 0 6px;
        }

        @container (min-width: 280px) {
            font-size: 14px;
            gap: 10px;
            text-transform: none;
            padding: 0 6px;
        }

        &__discount {
            align-self: center;
            padding: 0 4px;
            color: $color-cta;
            font-weight: 500;
            font-size: 11px;
            background: $color-white;
            border-radius: 10px;
            line-height: 25px;
            white-space: nowrap;

            @container (min-width: 280px) {
                font-size: 15px;
                padding: 0 5px;
            }

            &--above-50 {
                background: #000;
                color: $color-white;
            }
        }

        &__info-box {
            display: none;
            background: #fef4f2;
            position: absolute;
            bottom: calc(100% + 10px);
            left: 50%;
            margin-top: -10px;
            margin-left: -100px;
            width: 200px;
            text-transform: none;
            color: $color-dark;
            font-size: 14px;
            padding: 10px 10px;
            line-height: 18px;
            border-radius: 10px;
            z-index: 20;

            &::before {
                content: '';
                position: absolute;
                top: calc(100% - 5px);
                left: 50%;
                margin-left: -10px;
                width: 12px;
                height: 12px;
                background: #fef4f2;
                transform: rotate(45deg);
            }
        }

        &:hover {
            @include screen-md {
                .product-card-price__special-box__info-box {
                    display: block;
                }
            }
        }

        &--opened {
            .product-card-price__special-box__info-box {
                display: block;
            }
        }

        &--openers-price {
            background: $color-primary-green;
            color: $color-white;
            font-weight: 600;
            padding: 0 10px;
            font-size: 15px;
        }
    }
}
</style>
